<template>
  <div class="telemetry-table-history-view">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="DatabaseIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Telemetry History') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Complete telemetry data history') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="loadingHistory"
          @click="applyFilters"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': loadingHistory }"
          />
        </b-button>

        <b-button
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export to Excel')"
          :disabled="loadingHistoryExcel || loadingHistory"
          @click="exportToExcel"
        >
          <b-spinner
            v-if="loadingHistoryExcel"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export to Excel') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'telemetry-summary', params: { id: assetId } }"
        >
          <feather-icon
            icon="BarChart2Icon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Graphic Analysis') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'queries-weekly-work-status', params: { id: assetId } }"
        >
          <feather-icon
            icon="BriefcaseIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Work Status') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Asset Summary Header Card -->
    <b-card
      no-body
      class="asset-header-card mb-2 shadow-sm"
    >
      <div class="asset-header-content p-1 p-md-1">
        <!-- Skeleton Loading State -->
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center justify-content-between flex-wrap w-100 py-50"
        >
          <div class="d-flex align-items-center">
            <b-spinner
              variant="primary"
              small
              class="mr-1"
            />
            <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
          </div>
        </div>

        <!-- Loaded Content -->
        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <!-- Left: Back Button, Avatar, Plate, Marca, Modelo -->
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <!-- Back Button -->
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="$router.back()"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <!-- Vehicle Avatar -->
            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || $t('Model')"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <!-- Vehicle Identifiers: Plate + Marca / Modelo -->
            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <!-- Plate Badge -->
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <!-- Marca & Modelo -->
                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetId }}
                  </h5>
                </div>

                <!-- Nickname Tag if any -->
                <b-badge
                  v-if="assetData && assetData.nickname"
                  variant="light-primary"
                  class="ml-50 badge-glow font-weight-normal"
                >
                  {{ assetData.nickname }}
                </b-badge>
              </div>

              <!-- Subtitle with Chassis if no plate -->
              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <!-- Right: Chassis Chip (when plate exists) & Action Button to Asset Summary -->
          <div class="d-flex align-items-center flex-wrap asset-meta-chips">
            <!-- Chassis Chip (only shown when plate is already shown on the left) -->
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer mr-1 mb-50 mb-md-0"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
              <feather-icon
                icon="CopyIcon"
                size="12"
                class="ml-50 text-muted copy-icon"
              />
            </div>

            <div
              v-if="assetGroupName"
              class="meta-chip mr-1 mb-50 mb-md-0"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <div
              v-if="assetCustomerName"
              class="meta-chip mr-1 mb-50 mb-md-0"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <div class="asset-actions flex-shrink-0">
              <b-button
                v-b-tooltip.hover
                variant="outline-primary"
                size="sm"
                class="btn-view-details"
                :to="{ name: 'apps-asset-summary', params: { id: assetId } }"
                :title="$t('goToAssetSummary')"
              >
                <feather-icon
                  icon="ActivityIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('assetSummary') }}</span>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <transition name="slide-fade">
      <b-card
        v-if="showFilters"
        no-body
        class="mb-1 shadow-sm border-0 filters-card"
      >
        <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="SlidersIcon"
              size="16"
              class="text-primary mr-50"
            />
            <h6 class="mb-0 font-weight-bolder">
              {{ $t('Filters') }}
            </h6>
            <b-badge
              v-if="activeFiltersCount > 0"
              variant="light-primary"
              pill
              class="ml-50"
            >
              {{ $t('{count} active', { count: activeFiltersCount }) }}
            </b-badge>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 6px;"
          >
            <b-button
              v-b-tooltip.hover
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              :title="$t('Clear Filters')"
              @click="resetFilters"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon"
              :title="$t('Close Filters')"
              @click="showFilters = false"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-button>
          </div>
        </b-card-header>

        <b-card-body class="p-1">
          <div
            class="mb-1 d-flex flex-wrap align-items-center"
            style="gap: 6px;"
          >
            <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
            <b-badge
              v-for="preset in periodPresets"
              :key="preset.id"
              href="javascript:void(0)"
              :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
              pill
              class="cursor-pointer py-50 px-75"
              @click="setDatePreset(preset.id)"
            >
              {{ preset.label }}
            </b-badge>
          </div>

          <b-row>
            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('Start Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateStart"
                  class="form-control"
                  :config="dateTimePickerConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>

            <b-col
              cols="12"
              sm="6"
              lg="4"
              class="mb-1"
            >
              <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                <feather-icon
                  icon="CalendarIcon"
                  size="13"
                  class="mr-25 text-primary"
                />
                <span>{{ $t('End Date') }}</span>
                <span class="text-danger ml-25">*</span>
              </label>
              <b-input-group size="sm">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                  />
                </b-input-group-prepend>
                <flat-pickr
                  v-model="dateEnd"
                  class="form-control"
                  :config="dateTimePickerConfig"
                  :placeholder="$t('Select...')"
                  @on-change="onCustomDateChange"
                />
              </b-input-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
            <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
              <feather-icon
                icon="InfoIcon"
                size="14"
                class="mr-50 text-primary flex-shrink-0"
              />
              <span>
                {{ $t('Vehicle:') }}
                <strong class="text-primary">{{ assetModel || assetPlate || assetChassis || assetId }}</strong>.
                {{ $t('Maximum search period is 31 days') }}
              </span>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 8px;"
            >
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="resetFilters"
              >
                {{ $t('Restore Default') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="d-inline-flex align-items-center"
                :disabled="loadingHistory || !dateStart || !dateEnd"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="loadingHistory"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-50"
                />
                <span>{{ $t('Apply Filters') }}</span>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </transition>

    <div
      v-if="!showFilters"
      class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
      style="gap: 6px;"
    >
      <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
      <b-badge
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center"
      >
        <feather-icon
          icon="LockIcon"
          size="12"
          class="mr-25"
        />
        {{ assetModel || assetPlate || assetChassis || assetId }}
      </b-badge>
      <b-badge
        v-if="periodLabel"
        variant="light-primary"
        pill
        class="d-inline-flex align-items-center cursor-pointer"
        @click="showFilters = true"
      >
        <feather-icon
          icon="CalendarIcon"
          size="12"
          class="mr-25"
        />
        {{ periodLabel }}
      </b-badge>
      <b-link
        class="font-small-2 font-weight-bold ml-50 text-danger"
        @click="resetFilters"
      >
        {{ $t('Clear') }}
      </b-link>
    </div>

    <b-card
      v-if="loadingHistory && rows.length === 0"
      class="shadow-sm border-0 text-center py-3"
    >
      <b-spinner
        variant="primary"
        class="mb-1"
      />
      <div class="text-muted font-small-3">
        {{ $t('Searching telemetry') }}
      </div>
    </b-card>

    <b-card
      v-else
      class="shadow-sm border-0"
    >
      <b-row>
        <b-col
          xl="12"
          md="12"
          sm="12"
        >
          <div
            class="d-flex align-items-center justify-content-between flex-wrap mb-1"
            style="gap: 8px;"
          >
            <small class="text-muted">
              <template v-if="orderedColumns.length">
                {{ visibleColumns.length }} {{ $t('of') }} {{ orderedColumns.length }} {{ $t('columns selected') }}
                · {{ $t('Drag to reorder') }}
              </template>
            </small>
            <b-dropdown
              variant="outline-primary"
              size="sm"
              right
              no-caret
              boundary="viewport"
              menu-class="telemetry-column-menu"
              :disabled="!orderedColumns.length"
            >
              <template #button-content>
                <feather-icon
                  icon="ColumnsIcon"
                  size="14"
                  class="mr-50"
                />
                {{ $t('Columns') }}
              </template>
              <div
                class="telemetry-column-panel"
                @click.stop
                @mousedown.stop
              >
                <div class="d-flex justify-content-between align-items-center mb-50">
                  <small class="text-muted font-weight-bold">{{ $t('Select Columns') }}</small>
                  <div>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-primary"
                      @click="showAllColumns"
                    >
                      {{ $t('All') }}
                    </b-button>
                    <span class="mx-50 text-muted">|</span>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-primary"
                      @click="hideOptionalColumns"
                    >
                      {{ $t('None') }}
                    </b-button>
                  </div>
                </div>
                <b-form-input
                  v-model="columnQuery"
                  size="sm"
                  class="mb-50"
                  :placeholder="$t('Search')"
                />
                <small
                  v-if="columnSearchActive"
                  class="d-block text-muted mb-50"
                >
                  {{ $t('Clear the search to reorder columns') }}
                </small>
                <draggable
                  v-model="columnLayout"
                  handle=".column-drag-handle"
                  :disabled="columnSearchActive"
                  animation="180"
                  ghost-class="column-option-ghost"
                  class="telemetry-column-options"
                >
                  <div
                    v-for="column in columnLayout"
                    :key="column.key"
                    class="column-option"
                  >
                    <button
                      type="button"
                      class="column-drag-handle"
                      :disabled="columnSearchActive"
                      :title="$t('Drag to reorder')"
                    >
                      <feather-icon
                        icon="MenuIcon"
                        size="14"
                      />
                    </button>
                    <b-form-checkbox
                      :checked="isColumnVisible(column.key)"
                      :disabled="isLastVisibleColumn(column.key)"
                      @change="toggleColumn(column.key)"
                    >
                      <span class="column-option-text">{{ column.label }}</span>
                    </b-form-checkbox>
                  </div>
                </draggable>
                <div class="border-top pt-50 mt-50">
                  <div class="d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      {{ visibleColumns.length }} {{ $t('of') }} {{ orderedColumns.length }} {{ $t('columns selected') }}
                    </small>
                    <b-button
                      size="sm"
                      variant="link"
                      class="p-0 text-primary"
                      @click="resetColumnLayout"
                    >
                      {{ $t('Restore Default') }}
                    </b-button>
                  </div>
                  <small class="d-block text-muted mt-25">
                    {{ $t('At least one column stays visible') }}
                    {{ $t('This column layout is saved for this asset') }}
                  </small>
                </div>
              </div>
            </b-dropdown>
          </div>
          <div class="tableFixHead">
            <b-table
              ref="refTelemetryTable"
              class="telemetry-draggable-table"
              sticky-header="37rem"
              striped
              small
              hover
              style="width: 100%; overflow-x: auto;"
              :items="rows"
              :fields="visibleColumns"
              primary-key="id"
              show-empty
              :empty-text="emptyText"
            >
              <template #head()="data">
                <span
                  class="column-table-drag"
                  :class="{
                    'is-dragging': draggingColumnKey === data.column,
                    'is-drop-target': dropTargetColumnKey === data.column && draggingColumnKey !== data.column,
                  }"
                  draggable="true"
                  :title="$t('Drag to reorder')"
                  @dragstart="onHeaderDragStart(data.column, $event)"
                  @dragover.prevent="onHeaderDragOver(data.column)"
                  @drop.prevent="onHeaderDrop(data.column)"
                  @dragend="onHeaderDragEnd"
                >
                  <feather-icon
                    icon="MenuIcon"
                    size="12"
                    class="column-table-grip"
                  />
                  <span>{{ data.label }}</span>
                </span>
              </template>
              <template #cell(dateTime)="data">
                <div class="font-weight-bold d-block text-nowrap">
                  {{ data.item.dateTime }}
                </div>
              </template>

              <template #cell(location)="data">
                <div class="d-flex align-items-center justify-content-center">
                  <span v-if="data.item._lat && data.item._lng" class="mr-50 text-muted font-small-3 font-weight-bold">
                    {{ data.item._lat }}, {{ data.item._lng }}
                  </span>
                  <b-button
                    v-if="data.item._lat && data.item._lng"
                    v-b-tooltip.hover
                    :href="`https://www.google.com/maps/search/?api=1&query=${data.item._lat},${data.item._lng}`"
                    target="_blank"
                    variant="primary"
                    size="sm"
                    class="btn-icon"
                    :title="$t('View on map')"
                  >
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                    />
                  </b-button>
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </div>
              </template>

            </b-table>
            <div class="text-center my-2">
              <b-button
                v-if="showBtnSeeMore && !loading"
                variant="primary"
                @click="loadMoreTelemetry()"
              >
                {{ $t('See more') }}
              </b-button>
              <b-spinner
                v-if="loading"
                variant="primary"
                :label="$t('Loading')"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle, import/no-cycle, import/no-extraneous-dependencies */
import {
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BDropdown,
  BFormCheckbox,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import draggable from 'vuedraggable'

import router from '@/router'

import store from '@/store'

import { resolveIcon } from '@/utils/assetUtils'
import defaultCarIcon from '@/assets/images/assets/Car.png'

import flatPickr from 'vue-flatpickr-component'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'

import queriesStoreModule from '../queriesStoreModule'
import useTelemetryHistoryColumns from './useTelemetryHistoryColumns'

const formatDateTimeLocal = (date, time) => {
  if (!date || !time) return ''
  const [year, month, day] = date.split('-')
  const [hours, minutes] = time.split(':')
  return `${year}-${month}-${day}T${hours}:${minutes}:00`
}

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BDropdown,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    draggable,
    flatPickr,
  },
  data() {
    return {
      showFilters: false,
      dateStart: '',
      dateEnd: '',
      dateRangePreset: 'today',
      ignoreDateChange: false,
      dateTimePickerConfig: {
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y-m-d H:i',
        altInput: true,
        altFormat: 'd/m/Y H:i',
        altInputClass: 'form-control form-control-sm',
        locale: getFlatpickrLocale(),
      },

      // Asset Info
      assetData: null,
      loadingAsset: false,

      // Form (partes usadas pela API)
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      page: 1,
      loading: false,

      // Table
      pageLength: 25,
      rows: [],
      showBtnSeeMore: true,
      emptyText: '',
      loadingHistory: false,
      loadingHistoryExcel: false,
    }
  },
  computed: {
    assetPlate() {
      return this.assetData?.plate || ''
    },
    assetChassis() {
      return this.assetData?.chassisNumber || ''
    },
    assetManufacturer() {
      return this.assetData?.manufacturerName || this.assetData?.brandName || this.assetData?.manufacturer || ''
    },
    assetModel() {
      if (!this.assetData) return ''
      const name = this.assetData.modelIdName || this.assetData.modelName || ''
      const year = this.assetData.modelYear ? `(${this.assetData.modelYear})` : ''
      return `${name} ${year}`.trim()
    },
    assetAvatar() {
      if (!this.assetData) return resolveIcon(null, null)
      return resolveIcon(this.assetData.assetType, this.assetData.iconUrl)
    },
    assetGroupName() {
      return this.assetData?.assetGroupName || this.assetData?.groupName || ''
    },
    assetCustomerName() {
      return this.assetData?.customerName || this.assetData?.ownerName || ''
    },
    activeFiltersCount() {
      return this.dateStart && this.dateEnd ? 1 : 0
    },
    periodLabel() {
      if (!this.dateStart || !this.dateEnd) return ''
      return `${this.formatPickerLabel(this.dateStart)} — ${this.formatPickerLabel(this.dateEnd)}`
    },
    periodPresets() {
      return [
        { id: 'today', label: this.$t('Today') },
        { id: 'yesterday', label: this.$t('Yesterday') },
        { id: 'last7', label: this.$t('Last 7 days') },
        { id: 'month', label: this.$t('This Month') },
      ]
    },
  },
  watch: {
    showFilters(isOpen) {
      if (!isOpen) return
      this.ignoreDateChange = true
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },
  },
  mounted() {
    this.emptyText = this.$t('No matching record found')
    this.setInitialDate()
    this.loadAssetData()
    this.composeReport(true)
  },
  methods: {
    formatPickerDate(date, hour) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}`
    },

    formatPickerLabel(value) {
      if (!value) return ''
      const [datePart, timePart = ''] = String(value).trim().split(' ')
      const [year, month, day] = datePart.split('-')
      if (!year || !month || !day) return String(value)
      const time = timePart.slice(0, 5)
      return time ? `${day}/${month}/${year} ${time}` : `${day}/${month}/${year}`
    },

    splitDateHour(value) {
      const [date = '', hour = '00:00'] = String(value || '').trim().split(' ')
      return { date, hour: hour.slice(0, 5) }
    },

    syncDateParts() {
      const start = this.splitDateHour(this.dateStart)
      const end = this.splitDateHour(this.dateEnd)
      this.startDate = start.date
      this.startTime = start.hour
      this.endDate = end.date
      this.endTime = end.hour
    },

    setInitialDate() {
      const today = new Date()
      this.ignoreDateChange = true
      this.dateStart = this.formatPickerDate(today, '00:00')
      this.dateEnd = this.formatPickerDate(today, '23:59')
      this.dateRangePreset = 'today'
      this.syncDateParts()
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },

    setDatePreset(preset) {
      this.ignoreDateChange = true
      const today = new Date()
      if (preset === 'today') {
        this.dateStart = this.formatPickerDate(today, '00:00')
        this.dateEnd = this.formatPickerDate(today, '23:59')
      } else if (preset === 'yesterday') {
        const day = new Date(today)
        day.setDate(day.getDate() - 1)
        this.dateStart = this.formatPickerDate(day, '00:00')
        this.dateEnd = this.formatPickerDate(day, '23:59')
      } else if (preset === 'month') {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        this.dateStart = this.formatPickerDate(start, '00:00')
        this.dateEnd = this.formatPickerDate(today, '23:59')
      } else {
        const start = new Date(today)
        start.setDate(today.getDate() - 6)
        this.dateStart = this.formatPickerDate(start, '00:00')
        this.dateEnd = this.formatPickerDate(today, '23:59')
      }
      this.dateRangePreset = preset
      this.syncDateParts()
      setTimeout(() => {
        this.ignoreDateChange = false
      }, 300)
    },

    onCustomDateChange() {
      if (this.ignoreDateChange) return
      this.dateRangePreset = ''
    },

    ensureDateOrder() {
      if (this.dateStart && this.dateEnd && this.dateStart > this.dateEnd) {
        const swap = this.dateStart
        this.dateStart = this.dateEnd
        this.dateEnd = swap
      }
      this.syncDateParts()
    },

    validateRange(tooLongMessage) {
      if (!this.dateStart || !this.dateEnd) {
        this.showFilters = true
        return false
      }
      this.ensureDateOrder()
      const startDateTime = new Date(`${this.startDate}T${this.startTime}`)
      const endDateTime = new Date(`${this.endDate}T${this.endTime}`)
      if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        this.showFilters = true
        return false
      }
      const differenceInDays = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 3600 * 24)
      if (differenceInDays > 31) {
        this.$toast.error(this.$t(tooLongMessage))
        this.showFilters = true
        return false
      }
      return true
    },

    applyFilters() {
      if (!this.validateRange('Maximum search period is 31 days')) return
      this.composeReport(true)
    },

    resetFilters() {
      this.setInitialDate()
      this.composeReport(true)
    },

    async loadAssetData() {
      if (!this.assetId) return
      this.loadingAsset = true
      try {
        const response = await store.dispatch('app-queries/fetchAsset', { id: this.assetId })
        if (response && response.data) {
          this.assetData = response.data
        }
      } catch (error) {
        console.error('Error fetching asset details:', error)
      } finally {
        this.loadingAsset = false
      }
    },

    handleImageError(e) {
      e.target.src = defaultCarIcon
    },

    copyToClipboard(text, label = '') {
      if (!text || text === '—') return
      this.$copyText(text)
        .then(() => {
          this.$toast.success(
            label ? `${label} ${this.$t('Copied successfully!')}` : this.$t('Copied successfully!'),
          )
        })
        .catch(() => {
          this.$toast.error(this.$t('An unexpected error occurred'))
        })
    },

    async exportToExcel() {
      if (!this.validateRange('Maximum export period is 31 days')) return

      this.loadingHistoryExcel = true

      try {
        const startDate = formatDateTimeLocal(this.startDate, this.startTime)
        const endDate = formatDateTimeLocal(this.endDate, this.endTime)
        const response = await store.dispatch('app-queries/fetchHistoryTelemetryTableExcel', {
          assetId: this.assetId,
          startDate,
          endDate,
          page: 1,
        })

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `telemetria_${this.startDate}_${this.endDate}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        this.$toast.success(this.$t('Data exported successfully to Excel'))
      } catch (error) {
        this.$toast.error(this.$t('Error exporting data to Excel'))
      } finally {
        this.loadingHistoryExcel = false
      }
    },

    loadMoreTelemetry() {
      this.page += 1
      this.composeReport(false)
    },

    formatResponse(response) {
      if (response.headerNames.length > 0 && response.data && response.header) {
        this.responseToColumns(response)
        this.responseToRows(response)
      }
    },

    responseToColumns(response) {
      const headerNames = []
      let hasLocation = false
      let latKey = null
      let lngKey = null

      headerNames.push({
        key: 'dateTime',
        label: this.$t('Date'),
        sortable: false,
        thStyle: { textTransform: 'none' },
      })

      response.headerNames.forEach((name, index) => {
        const key = response.header[index]
        const lowerName = name.toLowerCase()
        const lowerKey = key.toLowerCase()

        if (lowerName === 'latitude' || lowerKey === 'latitude' || lowerName === 'lat' || lowerKey === 'lat') {
          hasLocation = true
          latKey = key
        } else if (lowerName === 'longitude' || lowerKey === 'longitude' || lowerName === 'lng' || lowerKey === 'lng' || lowerName === 'lon' || lowerKey === 'lon') {
          hasLocation = true
          lngKey = key
        } else {
          headerNames.push({
            key,
            label: name,
            sortable: false,
            class: 'my-left-border',
            thStyle: { textTransform: 'none' },
          })
        }
      })

      if (hasLocation) {
        headerNames.push({
          key: 'location',
          label: this.$t('Location'),
          sortable: false,
          class: 'text-center my-left-border',
          thStyle: { textTransform: 'none' },
        })
        this.latKey = latKey
        this.lngKey = lngKey
      } else {
        this.latKey = null
        this.lngKey = null
      }

      this.setColumnCatalog(headerNames)
    },

    responseToRows(response) {
      response.data.forEach(data => {
        const jsonResult = {}
        response.header.forEach((key, index) => {
          jsonResult[key] = data.data[index]
        })
        jsonResult.dateTime = this.formatDate(new Date(data.dateTime))

        if (this.latKey && this.lngKey) {
          const lat = jsonResult[this.latKey]
          const lng = jsonResult[this.lngKey]
          if (lat && lng && parseFloat(lat) !== 0 && parseFloat(lng) !== 0) {
            jsonResult._lat = lat
            jsonResult._lng = lng
          }
        }

        if (jsonResult) this.rows.push(jsonResult)
      })
    },

    formatDate(date) {
      if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '-'
      const localDate = this.convertUTCDateToLocalDate(date)
      const day = String(localDate.getDate()).padStart(2, '0')
      const month = String(localDate.getMonth() + 1).padStart(2, '0')
      const year = localDate.getFullYear()
      const hours = String(localDate.getHours()).padStart(2, '0')
      const minutes = String(localDate.getMinutes()).padStart(2, '0')
      const seconds = String(localDate.getSeconds()).padStart(2, '0')

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    },

    convertUTCDateToLocalDate(date) {
      const newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000)
      const offset = date.getTimezoneOffset() / 60
      const hours = date.getHours()
      newDate.setHours(hours - offset)
      return newDate
    },

    resetTable() {
      this.rows = []
      this.page = 1
      this.showBtnSeeMore = false
    },

    async composeReport(newRequest) {
      if (this.loading === true) return
      this.loading = true
      this.loadingHistory = true
      this.emptyText = this.$t('Searching telemetry')
      if (newRequest) {
        this.resetTable()
      }

      try {
        const startDate = formatDateTimeLocal(this.startDate, this.startTime)
        const endDate = formatDateTimeLocal(this.endDate, this.endTime)

        const response = await this.fetchTelemetryHistory(this.page, startDate, endDate)
        if (response.data) {
          this.formatResponse(response.data)
          this.showBtnSeeMore = true
        }

        if (response.data && response.data.data && response.data.data.length < this.pageLength) this.showBtnSeeMore = false
        this.loading = false
        this.loadingHistory = false
        this.emptyText = this.$t('No matching record found')
      } catch (e) {
        this.resetTable()
        this.loading = false
        this.loadingHistory = false
        this.showBtnSeeMore = false
        this.emptyText = this.$t('No matching record found')
        this.$toast.error(this.$t('An unexpected error occurred'))
      }
    },

    async fetchTelemetryHistory(page, startDate, endDate) {
      const response = await store.dispatch('app-queries/fetchTelemetryHistory', {
        assetId: this.assetId,
        startDate,
        endDate,
        page,
        perPage: this.pageLength,
      })
      return response
    },
  },
  setup() {
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'

    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)

    onUnmounted(() => {
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
    })

    const assetId = ref(router.currentRoute.params.id)
    const columnState = useTelemetryHistoryColumns(assetId)

    return {
      QUERIES_APP_STORE_MODULE_NAME,
      assetId,
      ...columnState,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.dropdown-menu.telemetry-column-menu {
  min-width: 320px !important;
  max-width: 360px !important;
  border-radius: 0.5rem;
  padding: 0.75rem !important;

  .telemetry-column-options {
    max-height: 280px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 0 -0.25rem;
    padding-right: 0.25rem;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d8d6de;
      border-radius: 4px;
    }
  }

  .column-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0.35rem 0.25rem;
    border-radius: 0.4rem;

    &:hover {
      background: rgba(115, 103, 240, 0.06);
    }
  }

  .column-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    color: #b9b9c3;
    cursor: grab;
    flex-shrink: 0;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.35;
    }
  }

  .column-option-ghost {
    opacity: 0.45;
    background: rgba(115, 103, 240, 0.12);
  }

  .column-option-text {
    font-size: 0.875rem;
    line-height: 1.35;
  }

  .custom-control {
    min-width: 0;
    margin-bottom: 0;
  }
}

body.dark-layout .dropdown-menu.telemetry-column-menu {
  .telemetry-column-options::-webkit-scrollbar-thumb {
    background: #3b4253;
  }

  .column-option:hover,
  .column-option-ghost {
    background: rgba(115, 103, 240, 0.16);
  }
}

.telemetry-table-history-view {
  .telemetry-draggable-table {
    th .column-table-drag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      max-width: 100%;
      cursor: grab;
      user-select: none;

      &.is-dragging {
        opacity: 0.4;
      }

      &.is-drop-target {
        box-shadow: inset 3px 0 0 #7367f0;
        padding-left: 4px;
      }
    }

    th .column-table-grip {
      color: #b9b9c3;
      flex-shrink: 0;
    }
  }

  .spin-icon {
    animation: telemetry-table-spin 1s linear infinite;
  }

  .filters-card {
    border-radius: 0.5rem;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes telemetry-table-spin {
  100% {
    transform: rotate(360deg);
  }
}

.b-table .my-left-border {
  border-left: 1px solid #d1d0d3;
  font-size: smaller !important;
}

// Asset Header Card Styling
.asset-header-card {
  border: 1px solid rgba(115, 103, 240, 0.14) !important;
  border-left: 4px solid #7367f0 !important;
  border-radius: 0.5rem;
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;

  .asset-header-content {
    min-height: 60px;
    display: flex;
    align-items: center;
  }

  .back-btn {
    border: 1px solid rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
    &:hover {
      background-color: rgba(115, 103, 240, 0.1) !important;
      color: #7367f0 !important;
      border-color: #7367f0;
    }
  }

  .asset-avatar-box {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

    .asset-avatar-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 4px;
      transition: transform 0.2s ease;
    }

    &:hover .asset-avatar-img {
      transform: scale(1.08);
    }
  }

  .license-plate-badge {
    display: inline-flex;
    align-items: center;
    background: #ffffff;
    border: 2px solid #2b2b2b;
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 1px;
    color: #2b2b2b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.15s ease;

    .plate-number {
      font-family: 'Consolas', 'Courier New', monospace;
    }

    .copy-icon {
      opacity: 0.6;
      transition: opacity 0.15s ease;
    }

    &:hover {
      border-color: #7367f0;
      color: #7367f0;
      background-color: rgba(115, 103, 240, 0.05);

      .copy-icon {
        opacity: 1;
        color: #7367f0 !important;
      }
    }
  }

  .asset-brand-text {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
    color: #7367f0 !important;
  }

  .asset-model-title {
    font-size: 1.05rem;
    letter-spacing: -0.2px;
  }

  .asset-meta-chips {
    gap: 0.5rem;

    .meta-chip {
      display: inline-flex;
      align-items: center;
      background: #f8f9fa;
      border: 1px solid #ebedf0;
      border-radius: 20px;
      padding: 3px 10px;
      font-size: 0.8rem;
      transition: all 0.15s ease;
      cursor: default;

      &.cursor-pointer, &[class*="cursor-pointer"] {
        cursor: pointer;
        &:hover {
          background: #f0f2f5;
          border-color: #7367f0;
        }
      }

      .meta-label {
        font-weight: 600;
        color: #6e6b7b;
        margin-right: 4px;
      }

      .meta-value {
        font-weight: 500;
        color: #5e5873;
      }

      .copy-icon {
        opacity: 0.6;
        transition: opacity 0.15s ease;
      }

      &:hover .copy-icon {
        opacity: 1;
        color: #7367f0 !important;
      }
    }
  }

  .btn-view-details {
    font-weight: 600;
    border-radius: 6px;
    padding: 0.45rem 0.85rem;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
  }
}

// Dark layout support
body.dark-layout,
.dark-layout {
  .asset-header-card {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    border-left-color: #7367f0 !important;

    .back-btn {
      border-color: rgba(255, 255, 255, 0.12);
      color: #b4b7bd;
    }

    .asset-avatar-box {
      background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .license-plate-badge {
      background: #161d31;
      border-color: #4b4b5a;
      color: #d0d2d6;

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
        background-color: rgba(115, 103, 240, 0.1);
      }
    }

    .asset-brand-text {
      color: #9086f4 !important;
    }

    .asset-model-title {
      color: #d0d2d6 !important;
    }

    .asset-meta-chips {
      .meta-chip {
        background: #161d31;
        border-color: rgba(255, 255, 255, 0.08);

        &.cursor-pointer:hover {
          background: #1e2433;
          border-color: #7367f0;
        }

        .meta-label {
          color: #b4b7bd;
        }

        .meta-value {
          color: #d0d2d6;
        }
      }
    }
  }
}
</style>
